import { useCallback, useMemo } from "react";
import { log } from "../../../util/log";
import { activeProfile } from "../../../util/selectors";
import StarterInfo from "../../../util/StarterInfo";
import { useToolsData } from "./useToolsData";
import { useToolsRunning } from "./useToolsRunning";
import { useToolsValidation } from "./useToolsValidation";
const MAX_VISIBLE_TOOLS = 5;
/**
 * Hook to access tools data for the current game.
 * Composes useToolsData, useToolsValidation, and useToolsRunning.
 * Provides visible tools, primary starter, running state, and actions.
 */
export const useTools = (onShowError, api) => {
    const { gameId, gameStarter, tools, discoveredTools, discoveryPath, primaryToolId, pinnedToolsMap, deploymentCounter, } = useToolsData();
    // Get all starters for validation
    const allStarters = useMemo(() => (gameStarter ? [gameStarter, ...tools] : tools), [gameStarter, tools]);
    const { isToolValid } = useToolsValidation(allStarters, discoveryPath, deploymentCounter);
    const { exclusiveRunning, isToolRunning } = useToolsRunning();
    // Determine the primary starter (tool or game)
    const primaryStarter = useMemo(() => {
        if (primaryToolId) {
            const primaryTool = tools.find((tool) => tool.id === primaryToolId);
            if (primaryTool?.exePath) {
                return primaryTool;
            }
        }
        return gameStarter;
    }, [primaryToolId, tools, gameStarter]);
    // Check if primary starter is running
    const isPrimaryRunning = useMemo(() => isToolRunning(primaryStarter?.exePath ?? ""), [isToolRunning, primaryStarter?.exePath]);
    // Show pinned tools matching the Tools page pinned section.
    // Hidden tools (removed in classic) are excluded. Only explicitly pinned tools show.
    // Excludes the launcher (shown in Play button). Capped at MAX_VISIBLE_TOOLS for the sidebar.
    const visibleTools = useMemo(() => {
        const nonLauncher = tools.filter((starter) => starter.id !== primaryToolId);
        // Exclude hidden tools, then only show explicitly pinned ones
        const pinned = nonLauncher.filter((starter) => (discoveredTools[starter.id] === undefined ||
            discoveredTools[starter.id].hidden !== true) &&
            pinnedToolsMap[starter.id] === true);
        return pinned.slice(0, MAX_VISIBLE_TOOLS);
    }, [tools, discoveredTools, primaryToolId, pinnedToolsMap]);
    const startTool = useCallback((info) => {
        if (info?.exePath === undefined) {
            onShowError("Tool missing/misconfigured", "Please ensure that the tool/game is configured correctly and try again", false);
            return;
        }
        api.events.emit("analytics-track-click-event", "Tools", "Manually ran tool");
        StarterInfo.run(info, api, onShowError);
    }, [api, onShowError]);
    const handlePlay = useCallback(() => {
        if (!primaryStarter) {
            onShowError("No game configured", "Please ensure that a game is configured and discovered", false);
            return;
        }
        api.events.emit("analytics-track-click-event", "Menu", "Play game");
        const state = api.getState();
        const profile = activeProfile(state);
        const currentModsState = profile?.modState ?? {};
        const enabledMods = Object.keys(currentModsState).filter((modId) => currentModsState?.[modId]?.enabled ?? false);
        log("info", `Enabled mods at game launch: ${enabledMods.length}`);
        StarterInfo.run(primaryStarter, api, onShowError);
    }, [primaryStarter, api, onShowError]);
    return {
        gameId,
        visibleTools,
        primaryStarter,
        primaryToolId,
        isPrimaryRunning,
        exclusiveRunning,
        isToolRunning,
        startTool,
        handlePlay,
    };
};
